import { DiscordSDK } from "@discord/embedded-app-sdk";
import "./style.css";
import { ssoWidgetUrl, widgetUrl } from "./nolt-url";

interface ActivityConfig {
  discordClientId: string;
  noltBoardUrl: string;
  noltMappingPrefix: string;
  withHeader: boolean;
  ssoEnabled: boolean;
}

const appElement = document.querySelector<HTMLDivElement>("#app");

if (!appElement) {
  throw new Error("Missing #app root element");
}

const app: HTMLDivElement = appElement;

let discordSdk: DiscordSDK | undefined;

type ActivityAuthStage =
  | "page_loaded"
  | "sdk_connecting"
  | "sdk_ready"
  | "authorize_started"
  | "authorize_succeeded"
  | "token_succeeded"
  | "authenticate_succeeded"
  | "sso_succeeded"
  | "auth_failed";

function reportActivityStage(stage: ActivityAuthStage): void {
  void fetch(`/api/activity-event?stage=${encodeURIComponent(stage)}`, {
    method: "POST",
    cache: "no-store",
    keepalive: true,
  }).catch(() => undefined);
}

function setStatus(message: string): void {
  const status = document.querySelector<HTMLElement>(".status-message");
  if (status) status.textContent = message;
}

function withTimeout<T>(
  promise: Promise<T>,
  milliseconds: number,
  message: string,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error(message)), milliseconds);
    }),
  ]);
}

function isDiscordProxy(): boolean {
  return window.location.hostname.endsWith(".discordsays.com");
}

async function initializeDiscord(clientId: string): Promise<DiscordSDK> {
  discordSdk ??= new DiscordSDK(clientId);
  await withTimeout(
    discordSdk.ready(),
    10_000,
    "Discord SDK readiness timed out.",
  );
  return discordSdk;
}

async function responseJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new Error("The Worker returned an invalid sign-in response.");
  }
}

async function authenticatedWidgetUrl(config: ActivityConfig): Promise<string> {
  const discordProxy = isDiscordProxy();

  if (!discordProxy || !config.ssoEnabled) {
    return widgetUrl(config, discordProxy);
  }

  try {
    setStatus("Connecting to Discord…");
    reportActivityStage("sdk_connecting");
    const sdk = await initializeDiscord(config.discordClientId);
    reportActivityStage("sdk_ready");

    setStatus("Waiting for Discord authorization…");
    reportActivityStage("authorize_started");
    const { code } = await withTimeout(
      sdk.commands.authorize({
        client_id: config.discordClientId,
        response_type: "code",
        state: "",
        scope: ["identify", "email"],
      }),
      30_000,
      "Discord authorization timed out. Close and reopen the Activity.",
    );
    reportActivityStage("authorize_succeeded");

    setStatus("Completing Discord authentication…");
    const tokenResponse = await fetch("/api/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ code }),
      signal: AbortSignal.timeout(15_000),
    });
    const tokenPayload = await responseJson<{
      accessToken?: string;
      error?: string;
    }>(tokenResponse);

    if (!tokenResponse.ok || !tokenPayload.accessToken) {
      throw new Error(
        tokenPayload.error || "Discord sign-in could not be completed.",
      );
    }
    reportActivityStage("token_succeeded");

    const auth = await withTimeout(
      sdk.commands.authenticate({
        access_token: tokenPayload.accessToken,
      }),
      15_000,
      "Discord authentication timed out. Close and reopen the Activity.",
    );

    if (
      !auth ||
      auth.application.id !== config.discordClientId ||
      !auth.scopes.includes("identify") ||
      !auth.scopes.includes("email")
    ) {
      throw new Error(
        "Discord did not authenticate the identify and email permissions required for Nolt.",
      );
    }
    reportActivityStage("authenticate_succeeded");

    setStatus("Signing in to Nolt…");
    const ssoResponse = await fetch("/api/sso", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        accessToken: tokenPayload.accessToken,
        discordUserId: auth.user.id,
      }),
      signal: AbortSignal.timeout(35_000),
    });
    const ssoPayload = await responseJson<{ token?: string; error?: string }>(
      ssoResponse,
    );

    if (!ssoResponse.ok || !ssoPayload.token) {
      throw new Error(ssoPayload.error || "Nolt sign-in could not be completed.");
    }
    reportActivityStage("sso_succeeded");

    return ssoWidgetUrl(
      config,
      ssoPayload.token,
      true,
      window.location.origin,
    );
  } catch (error) {
    reportActivityStage("auth_failed");
    throw error;
  }
}

async function render(config: ActivityConfig): Promise<void> {
  const discordProxy = isDiscordProxy();
  const needsAutomaticSso = discordProxy && config.ssoEnabled;

  if (needsAutomaticSso) {
    app.innerHTML =
      '<p class="status-message" role="status">Connecting to Discord…</p>';
    reportActivityStage("page_loaded");
  }

  const authenticatedUrl = await authenticatedWidgetUrl(config);

  app.innerHTML =
    '<iframe id="nolt-frame" title="Nolt feedback board" allow="clipboard-write"></iframe>';

  const frame = document.querySelector<HTMLIFrameElement>("#nolt-frame");

  if (!frame) {
    throw new Error("Nolt iframe could not be created.");
  }

  frame.src = authenticatedUrl;
}

function renderError(error: unknown): void {
  const message =
    error instanceof Error
      ? error.message
      : "The feedback board could not be configured.";

  app.innerHTML = '<p class="error-message" role="alert"></p>';
  const errorMessage = document.querySelector<HTMLElement>(".error-message");
  if (errorMessage) errorMessage.textContent = message;
}

async function start(): Promise<void> {
  const response = await fetch("/api/config", {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  const payload = (await response.json()) as
    | ActivityConfig
    | { error?: string };

  if (!response.ok) {
    throw new Error(
      "error" in payload && payload.error
        ? payload.error
        : "Worker configuration is incomplete.",
    );
  }

  await render(payload as ActivityConfig);
}

void start().catch(renderError);
